import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { GiftIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { SparklesIcon } from '@heroicons/react/24/solid';
import { toast } from 'react-hot-toast';

// Redux
import { setOrderDataAction } from '../../redux/Actions/CreateOrderActions';
import { getPromotionsApi } from '../../redux/Reducers/commonApis';

// Utils
import Transition from '../../utils/Transition';

export default function PromoSurpriseModal({ addressId, open, onClose }) {
  const dispatch = useDispatch();
  const [promos, setPromos] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !addressId) return;
    setLoading(true);
    getPromotionsApi(addressId).then((res) => {
      setLoading(false);
      if (res?.status === 200 || res?.status === 201) {
        const now = new Date();
        const active = (res?.data || []).filter((p) => {
          if (!p?.end_date) return true;
          return new Date(p.end_date) >= now;
        });
        setPromos(active);
        if (!active.length) onClose();
      } else {
        onClose();
      }
    });
  }, [open, addressId]);

  const applyCode = (code) => {
    dispatch(setOrderDataAction({ orderPromoCode: code }));
    toast.success(`Promo code "${code}" applied!`);
    onClose();
  };

  if (!open || (!loading && promos.length === 0)) return null;

  return (
    <>
      {/* Backdrop */}
      <Transition
        className='fixed inset-0 z-50 bg-slate-900/40 transition-opacity'
        show={open}
        enter='transition ease-out duration-200'
        enterStart='opacity-0'
        enterEnd='opacity-100'
        leave='transition ease-out duration-100'
        leaveStart='opacity-100'
        leaveEnd='opacity-0'
        aria-hidden='true'
      />
      {/* Modal */}
      <Transition
        className='fixed inset-0 z-50 flex items-center justify-center overflow-hidden px-4'
        role='dialog'
        aria-modal='true'
        show={open && !loading && promos.length > 0}
        enter='transition ease-out duration-300'
        enterStart='opacity-0 scale-95'
        enterEnd='opacity-100 scale-100'
        leave='transition ease-in duration-200'
        leaveStart='opacity-100 scale-100'
        leaveEnd='opacity-0 scale-95'
      >
        <div className='relative w-full max-w-2xl overflow-hidden rounded-2xl bg-white shadow-2xl'>
          {/* Close button */}
          <button
            type='button'
            className='absolute right-4 top-4 z-10 rounded-full p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600'
            onClick={onClose}
          >
            <XMarkIcon className='h-6 w-6' />
          </button>

          {/* Header */}
          <div className='px-8 pt-8 pb-6 text-center'>
            <div className='mx-auto mb-3 flex h-16 w-16 items-center justify-center rounded-full bg-emerald-50'>
              <GiftIcon className='h-9 w-9 text-emerald-500' />
            </div>
            <h3 className='text-2xl font-bold text-slate-800'>You've got offers!</h3>
            <p className='mt-1.5 text-sm text-slate-500'>Apply a promo code and save on your order</p>
          </div>

          {/* Divider */}
          <div className='mx-6 border-t border-slate-100' />

          {/* Promo list */}
          <div className='max-h-[28rem] space-y-3 overflow-y-auto px-6 py-5'>
            {promos.map((p) => (
              <div
                key={p.id}
                className='flex items-center gap-4 rounded-xl border border-slate-200 bg-white p-4 transition hover:border-emerald-200 hover:shadow-md'
              >
                <div className='flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-emerald-50'>
                  <SparklesIcon className='h-6 w-6 text-emerald-400' />
                </div>
                <div className='min-w-0 flex-1'>
                  <div className='flex items-center gap-2.5'>
                    <span className='rounded-md bg-emerald-600 px-2.5 py-1 text-xs font-bold tracking-wide text-white'>
                      {p.code}
                    </span>
                    <span className='text-sm font-bold text-slate-700'>
                      {p.type === 'PERCENTAGE_OFF' ? `${p.type_amount}% OFF` : `$${p.type_amount} OFF`}
                    </span>
                  </div>
                  {p.message && <p className='mt-1 truncate text-sm text-slate-400'>{p.message}</p>}
                </div>
                <button
                  type='button'
                  className='shrink-0 rounded-lg border-2 border-emerald-600 bg-white px-5 py-2 text-sm font-semibold text-emerald-600 transition hover:bg-emerald-600 hover:text-white'
                  onClick={() => applyCode(p.code)}
                >
                  Apply
                </button>
              </div>
            ))}
          </div>

          {/* Footer */}
          <div className='border-t border-slate-100 px-6 py-4 text-center'>
            <button type='button' className='text-sm font-medium text-slate-400 hover:text-slate-600' onClick={onClose}>
              Maybe later
            </button>
          </div>
        </div>
      </Transition>
    </>
  );
}
